import { createContext, type PropsWithChildren, useContext, useEffect, useState } from "react";
import { getTotalStock, hasInStock } from "~/utils/packageEntryUtils";
import { ProductService } from "~/features/addToCart/api/ProductService";
import {
  getPackages,
  getPackagesBySalesType,
  hasSuccessor,
} from "~/features/productDetails/components/PhaseInOut/phaseInOutHelper";
import { type PackagingInformationEntry, type ProductData } from "~/domains/product/types/ProductData";
import { isNonNull, splitBy } from "~/common/utils/reducers";
import { productHasInStock } from "~/domains/product/utils/productDescriptorUtils";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { SalesStatus } from "~/features/productDetails/components/PhaseInOut/SalesStatus";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

interface ResultData {
  successorBanner: "atc" | "packages" | "image" | false;
  discontinuedBanner: "product" | "package" | false;
  alternativeBanner: AlternativeBannerContext | false;
  purchaseLogicChange:
    | {
        type: "error" | "warn";
        packCodes: string[];
      }
    | false;

  showAdditionalSuccessorLink: boolean;
  changeATCLabelToQuote: boolean;

  preferredProductCodes: PreferredProductCodesContext | false;
}

export type AlternativeBannerContext =
  | {
      status: "loading";
    }
  | {
      status: "loaded";
      productCode: string;
      product: ProductDescriptor | ProductData;
    };

export interface PreferredProductCodesContext {
  status: "canLoad" | "loading" | "loaded";
  codes: string[];
  totalAmount: number;
}

export const usePhaseInOutLogic = ({
  product,
  preferredProductCodes,
}: {
  product: ProductDescriptor;
  preferredProductCodes: string[];
}): ResultData => {
  const [resultData, setResultData] = useState(DEFAULT_RESULT);

  useEffect(() => {
    if (!product?.availability) {
      return;
    }

    const { discontinuedPackages, ongoingPackages } = getPackagesBySalesType(product);

    const hasSuccessorProduct = hasSuccessor(product);
    const hasNoSuccessorProduct = !hasSuccessorProduct;

    const noDiscontinuedPackages = discontinuedPackages.length === 0;
    const hasDiscontinuedPackages = discontinuedPackages.length > 0;
    const noOngoingPackagesWithInStock = !ongoingPackages.some(hasInStock);
    const hasSomeOngoingPackagesWithStock = ongoingPackages.some(function (packaging: PackagingInformationEntry) {
      return getTotalStock(packaging) > 0;
    });
    const noOngoingPackagesWithStock = !hasSomeOngoingPackagesWithStock;
    const noOngoingPackages = ongoingPackages.length === 0;
    const hasOngoingPackages = ongoingPackages.length > 0;

    /**
     *  Successor message cases:
     *
     *  ongoing             discontinued              Successor     Successor
     *  amount  stock       amount  stock                           ATC button  above image   in packaging
     *  *       NO          0       -                 YES           NO          NO            YES
     *  0       -           1+      YES               YES           NO          YES           NO
     *  1       *           1+      YES               YES           YES         NO            NO
     *  1+      SOME        0       -                 YES           YES         NO            NO
     */
    let successorBanner: ResultData["successorBanner"] = false;
    if (hasSuccessorProduct) {
      if (noDiscontinuedPackages && noOngoingPackagesWithStock) {
        successorBanner = "packages";
      } else if (hasDiscontinuedPackages && noOngoingPackages) {
        successorBanner = "image";
      } else if (
        (noDiscontinuedPackages && hasSomeOngoingPackagesWithStock) ||
        (hasDiscontinuedPackages && hasOngoingPackages)
      ) {
        successorBanner = "atc";
      }
    }

    /**
     * Discontinued message cases:
     *
     * ongoing                discontinued    Successor     Discountinuation
     * amount   stock         amount  stock                 package level   product level
     *
     * 0        -             *       *       NO            NO              YES
     * 1        *             1+      YES     *             YES             NO
     */
    let discontinuedBanner: ResultData["discontinuedBanner"] = false;
    if (hasNoSuccessorProduct && noOngoingPackages) {
      discontinuedBanner = "product";
    } else if (hasOngoingPackages && hasDiscontinuedPackages) {
      discontinuedBanner = "package";
    }

    /**
     * purchase logic change cases:
     *
     * ongoing          discontinued    Successor     Purchase logic
     * amount   stock   amount  stock                 Buy only available qty  Warn about available qty
     *
     * *        *       1+      YES     YES           YES                     -
     * *        *       1+      YES     NO            NO                      YES
     */
    let purchaseLogicChange: ResultData["purchaseLogicChange"] = false;
    if (hasDiscontinuedPackages) {
      purchaseLogicChange = {
        type: hasSuccessorProduct ? "error" : "warn",
        packCodes: discontinuedPackages.map((p) => p.packCode),
      };
    }

    /**
     * Additional successor link cases:
     *
     * Ongoing packages    Discontinued packages    Successor    Extra
     * amount  stock  amount  stock                              Show link to successor by ATC
     *
     * 0       -      1+      YES                   YES          YES
     */
    let showAdditionalSuccessorLink: ResultData["showAdditionalSuccessorLink"] = false;
    if (hasSuccessorProduct && hasDiscontinuedPackages && noOngoingPackages) {
      showAdditionalSuccessorLink = true;
    }

    /**
     * Change ATC label to request a quote cases:
     *
     * Ongoing packages    Discontinued packages    Successor    Extra
     * amount  stock  amount  stock                              Change ATC label to request a quote
     *
     * 0       -      0       -                     NO          YES
     */
    let changeATCLabelToQuote: ResultData["changeATCLabelToQuote"] = false;
    if (noDiscontinuedPackages && noOngoingPackages) {
      changeATCLabelToQuote = true;
    }

    /**
     * The alternative banner can appear when:
     *
     * - there is no successor
     * - a) there is at least one ongoing package & all ongoing packages have no "in stock" OR
     * - b) there are no ongoing packages
     * - there are no discontinued packages with stock == no discontinued packages at all
     * - there are alternative products
     * - alternative products have "in stock"
     *
     * If there are no ongoing packages, then if we were supposed to show discontinuation banner on product level, we switch to package level instead. (How??)
     */
    if (hasNoSuccessorProduct && noOngoingPackagesWithInStock && noDiscontinuedPackages) {
      const moveDiscontinuedBannerToPackageLevel = discontinuedBanner === "product" && noOngoingPackages;
      // noinspection ES6MissingAwait
      (async () => {
        let pickedAlternative: ProductDescriptor | undefined = undefined;

        const alternatives = product.base.alternativeProducts;
        if (alternatives.length > 0) {
          setResultData((prev) => ({
            ...prev,
            alternativeBanner: {
              status: "loading",
            },
          }));
          const alternativeDescriptions = await Promise.all(
            alternatives.map(
              (a) => ProductService.getProductDescriptor(a.code, getInitialPriceReference(), { fetch: true }).promise,
            ),
          );

          pickedAlternative = alternativeDescriptions.filter(Boolean).find((a) => getPackages(a!).some(hasInStock));
        }

        if (pickedAlternative) {
          setResultData((prev) => ({
            ...prev,
            alternativeBanner: {
              status: "loaded",
              productCode: pickedAlternative!.base.code,
              product: pickedAlternative!,
            },
            discontinuedBanner: moveDiscontinuedBannerToPackageLevel ? "package" : prev.discontinuedBanner,
          }));
        } else {
          // We can try loading preferred products
          setResultData((prev) => ({
            ...prev,
            alternativeBanner: false,
            preferredProductCodes: { status: "canLoad", codes: [], totalAmount: 0 },
          }));
        }
      })();
    }
    setResultData((prev) => ({
      ...prev,
      successorBanner,
      discontinuedBanner,
      purchaseLogicChange,
      showAdditionalSuccessorLink,
      changeATCLabelToQuote,
    }));
  }, [product]);

  const canLoadPreferredProducts =
    resultData.preferredProductCodes && resultData.preferredProductCodes.status === "canLoad";
  useEffect(() => {
    (async () => {
      if (canLoadPreferredProducts && preferredProductCodes.length > 0) {
        setResultData((prev) => ({
          ...prev,
          preferredProductCodes: { status: "loading", codes: [], totalAmount: preferredProductCodes.length },
        }));
        // Load all the preferred products and pick the first three, prioritizing those with stock
        const preferredProductPromises = preferredProductCodes.map(
          (code) =>
            ProductService.getProductDescriptor(code, getInitialPriceReference(), {
              fetch: true,
              forceFetchBasicData: true,
            }).promise,
        );
        const products = (await Promise.all(preferredProductPromises)).filter(isNonNull);
        const [withStock, withoutStock] = splitBy(products, productHasInStock);
        const selectedProductCodes = [...withStock, ...withoutStock].slice(0, 3).map((p) => p.base.code);
        setResultData((prev) => ({
          ...prev,
          preferredProductCodes: {
            status: "loaded",
            codes: selectedProductCodes,
            totalAmount: preferredProductCodes.length,
          },
        }));
      }
    })();
  }, [canLoadPreferredProducts, preferredProductCodes]);

  return resultData;
};

export const PhaseInOutLogicProvider = ({ children, resultData }: PropsWithChildren<{ resultData: ResultData }>) => {
  return <PhaseInOutContext.Provider value={resultData}>{children}</PhaseInOutContext.Provider>;
};
export const usePhaseInOutLogicContext = () => useContext(PhaseInOutContext);

export const useIsPackagePhasedOut = (productDescriptor: ProductDescriptor, packCode: string) => {
  const phaseInOutContext = usePhaseInOutLogicContext();
  if (phaseInOutContext.discontinuedBanner === "package") {
    const thisPackageIsDiscontinued =
      productDescriptor.base.fifteenDigit?.[packCode]?.salesStatus === SalesStatus.Discontinued;
    const thereIsAnAlternative = !!phaseInOutContext.alternativeBanner;
    if (thisPackageIsDiscontinued || thereIsAnAlternative) {
      return true;
    }
  }
  return false;
};

const DEFAULT_RESULT: ResultData = {
  successorBanner: false,
  discontinuedBanner: false,
  purchaseLogicChange: false,
  showAdditionalSuccessorLink: false,
  changeATCLabelToQuote: false,
  alternativeBanner: false,
  preferredProductCodes: false,
} as const;

const PhaseInOutContext = createContext<ResultData>(DEFAULT_RESULT);

export function getPreferredProductCodes(phaseInData: ResultData) {
  return phaseInData.preferredProductCodes ? phaseInData.preferredProductCodes.codes : [];
}
